import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Pagination, Searchbar } from '@treezor/design-system';

import { IWallet } from '../../store';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { IUser } from '@/features/user/store/user.model';

import { useAction } from '@/hooks/useAction';

import { RadioCardAdvanced } from '@/ui-kit/radio-card-advanced/RadioCardAdvanced';

import { Wallet } from '@/features/wallets/components/Wallet';
import { WalletSkeleton } from '@/features/wallets/components/WalletSkeleton';
import { AddWallet } from '@/features/wallets/components/AddWallet/AddWallet';
import useUserWallets from '@/features/wallets/hooks/userWallets/useUserWallets';
import { TAppError } from '@/store/types/base.state';

interface IUserWalletProps {
  user: IUser;
}

interface IUserWalletListProps {
  user: IUser;
  wallets: IWallet[];
  isLoading: boolean;
  error: TAppError;
  searchValue: string;
  dispatchCurrentWallet: (wallet: IWallet) => void;
}

export const UserWalletList = ({
  user,
  wallets,
  isLoading,
  error,
  dispatchCurrentWallet,
  searchValue,
}: IUserWalletListProps) => {
  const { walletId } = useParams();
  const { t } = useTranslation();

  const hasWallets = useMemo(() => !!wallets?.length, [wallets?.length]);

  useLayoutEffect(() => {
    const element = document.getElementById(`profile-wallet-${walletId}`);

    if (!isLoading && element && wallets?.length) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [walletId, isLoading, wallets, hasWallets]);

  if (isLoading) return <WalletSkeleton />;

  if (error) return <EmptyState />;

  if (!wallets?.length)
    return (
      <EmptyState
        title={t('pages.currentProfiles.wallets.walletFilter.noResult.title')}
        state={t('pages.currentProfiles.wallets.walletFilter.noResult.description', { value: searchValue })}
        icon="search_v1"
      />
    );

  return (
    <div className="flex flex-col lightScrollBar gap-4 overflow-x-hidden overflow-y-scroll max-h-[calc(100vh-450px)] pr-3">
      {wallets?.map((wallet: IWallet) => (
        <RadioCardAdvanced
          data-testid={`profile-wallet-${wallet.walletId}`}
          id={`profile-wallet-${wallet.walletId}`}
          key={`profile-wallet-${wallet.walletId}`}
          isSelected={wallet.walletId === Number(walletId)}
          value={wallet.walletId}
          handleChange={(currentWalletId) => currentWalletId !== Number(walletId) && dispatchCurrentWallet(wallet)}
        >
          <Wallet wallet={wallet} user={user} />
        </RadioCardAdvanced>
      ))}
    </div>
  );
};

export const UserWallets = ({ user }: IUserWalletProps) => {
  const { t } = useTranslation();
  const { displayActionFromConfig } = useAction();
  const { wallets, error, isLoading, dispatchCurrentWallet, onHandleFilter, pagination, onPageChange, searchValue } =
    useUserWallets(user);

  return (
    <div className="flex flex-col gap-5 w-[420px]">
      <div className="flex justify-between items-center pr-3">
        <h5 className="typo-h5-bold text-secondary">{t('pages.currentProfiles.wallets.title')}</h5>
        {displayActionFromConfig('addWallet') && <AddWallet user={user} />}
      </div>

      {pagination?.totalPages === 1 && (
        <Searchbar
          size="s"
          type="text"
          placeholder={t('pages.currentProfiles.wallets.walletFilter.placeholder')}
          onChange={(value: string) => {
            onHandleFilter(value);
          }}
          className="pr-3"
        />
      )}

      <UserWalletList
        user={user}
        wallets={wallets}
        error={error}
        isLoading={isLoading}
        dispatchCurrentWallet={dispatchCurrentWallet}
        searchValue={searchValue}
      />
      {pagination?.totalPages > 1 && !error && (
        <Pagination
          {...pagination}
          activePage={pagination.currentPage - 1}
          itemCount={pagination.totalPages * pagination.itemPerPage}
          onPageChange={(item: { selected: number }) => onPageChange(Number(item.selected) + 1)}
          isFirstButtonVisible
          isLastButtonVisible
          className="flex justify-center max-w-[420px]"
          pageRangeDisplayed={4}
        />
      )}
    </div>
  );
};
